/**
 * Kit couture — logique du module (bloc produit + section "Compose ton kit").
 * Chargé en <script type="module"> : l'importmap du thème résout @shopify/events.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

if (!customElements.get('kit-couture-builder')) {
  class KitCoutureBuilder extends HTMLElement {
    connectedCallback() {
      this.formatter = new Intl.NumberFormat(this.dataset.locale || 'fr', {
        style: 'currency',
        currency: this.dataset.currency || 'EUR',
      });
      this.lengthFormatter = new Intl.NumberFormat(this.dataset.locale || 'fr', {
        maximumFractionDigits: 2,
      });
      this.addEventListener('change', (e) => {
        // bouton choisi → caler la quantité sur le nombre de boutons du patron
        const input = e.target;
        if (input.dataset && input.dataset.kitc === 'bouton' && input.value && this.dataset.boutonsQty) {
          const qtyInput = this.querySelector('[data-kitc-qty="bouton"]');
          if (qtyInput) qtyInput.value = this.clampQty(parseInt(this.dataset.boutonsQty, 10));
        }
        this.sync();
      });
      this.addEventListener('click', (e) => this.onClick(e));

      // Le bandeau s'efface dès que le récapitulatif complet est à l'écran
      this.recapVisible = false;
      const footer = this.querySelector('.kitc-footer');
      if (footer && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.recapVisible = entries[0].isIntersecting;
          this.sync();
        });
        this.observer.observe(footer);
      }

      this.sync();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    onClick(e) {
      // pop-up « En savoir plus » (tissus)
      const more = e.target.closest('[data-kitc-more]');
      if (more) {
        const dialog = this.querySelector(`#${CSS.escape(more.dataset.kitcMore)}`);
        if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
        return;
      }
      const close = e.target.closest('[data-kitc-close]');
      if (close) {
        const dialog = close.closest('dialog');
        if (dialog) dialog.close();
        return;
      }
      if (e.target.tagName === 'DIALOG') {
        e.target.close();
        return;
      }

      const mbtn = e.target.closest('[data-kitc-mminus], [data-kitc-mplus]');
      if (mbtn) {
        const input = mbtn.parentElement.querySelector('[data-kitc-mqty]');
        if (input) {
          input.value = this.clampQty(parseInt(input.value, 10) + (mbtn.hasAttribute('data-kitc-mplus') ? 1 : -1));
          this.sync();
        }
        return;
      }
      const minus = e.target.closest('[data-kitc-minus]');
      const plus = e.target.closest('[data-kitc-plus]');
      if (minus || plus) {
        const key = (minus || plus).dataset.kitcMinus || (minus || plus).dataset.kitcPlus;
        const input = this.querySelector(`[data-kitc-qty="${key}"]`);
        if (input) {
          input.value = this.clampQty(parseInt(input.value, 10) + (plus ? 1 : -1));
          this.sync();
        }
        return;
      }
      const atc = e.target.closest('[data-kitc-atc]');
      if (atc && !atc.disabled) this.addKit(atc);
    }

    clampQty(n) {
      return Math.max(1, Math.min(99, isNaN(n) ? 1 : n));
    }

    getQty(key) {
      const input = this.querySelector(`[data-kitc-qty="${key}"]`);
      if (!input) return 1;
      const clamped = this.clampQty(parseInt(input.value, 10));
      if (String(clamped) !== input.value) input.value = clamped;
      return clamped;
    }

    merceries() {
      return [...this.querySelectorAll('input[data-kitc="mercerie"]:checked')].map((input) => {
        const qtyInput = input.closest('.kitc-mchip').querySelector('[data-kitc-mqty]');
        const qty = this.clampQty(parseInt(qtyInput ? qtyInput.value : '1', 10));
        if (qtyInput && String(qty) !== qtyInput.value) qtyInput.value = qty;
        return { id: Number(input.value), title: input.dataset.title, price: Number(input.dataset.price), qty };
      });
    }

    metrageLabel(qty) {
      const cm = Number(this.dataset.unitCm || 0) * qty;
      if (!cm) return '';
      return cm < 100 ? `${cm} cm` : `${this.lengthFormatter.format(cm / 100)} m`;
    }

    selection() {
      const taille = this.querySelector('input[data-kitc="taille"]:checked');
      const tissu = this.querySelector('input[data-kitc="tissu"]:checked');
      const bouton = this.querySelector('input[data-kitc="bouton"]:checked');
      const etiquette = this.querySelector('input[data-kitc="etiquette"]:checked');
      let fil = null;
      if (tissu) {
        const group = this.querySelector(`[data-for-tissu="${tissu.dataset.tissuId}"]`);
        if (group) fil = group.querySelector('input[data-kitc="fil"]:checked');
      }
      const rechargeInput = this.querySelector('[data-kitc-recharge]');
      const recharge = !!rechargeInput && rechargeInput.checked;
      return { taille, tissu, bouton, fil, etiquette, recharge };
    }

    sync() {
      const { taille, tissu, bouton, fil, etiquette, recharge } = this.selection();

      // Mode recharge : masquer l'étape taille (sauf si déjà masquée car taille unique)
      const tailleStep = this.querySelector('[data-kitc-taille-step]');
      if (tailleStep) tailleStep.hidden = recharge || tailleStep.hasAttribute('data-kitc-single');
      const cadeauGift = this.querySelector('[data-kitc-cadeau-gift]');
      if (cadeauGift) cadeauGift.hidden = recharge;
      const rechargeGift = this.querySelector('[data-kitc-recharge-gift]');
      if (rechargeGift) rechargeGift.hidden = !recharge;

      // Fils : afficher le groupe du tissu choisi, remettre les autres sur leur premier fil
      let filGroupVisible = false;
      this.querySelectorAll('[data-for-tissu]').forEach((group) => {
        const active = !!tissu && group.dataset.forTissu === tissu.dataset.tissuId;
        group.hidden = !active;
        if (active) filGroupVisible = true;
        else {
          const first = group.querySelector('input[data-kitc="fil"]');
          if (first) first.checked = true;
        }
      });
      const filStep = this.querySelector('[data-kitc-fil-step]');
      if (filStep) filStep.hidden = !filGroupVisible;

      // Lignes de quantité
      const tissuQtyRow = this.querySelector('[data-kitc-qtyrow="tissu"]');
      if (tissuQtyRow) tissuQtyRow.hidden = !tissu;

      // Alerte métrage : la quantité de tissu doit atteindre le minimum
      const tissuMin = Number(this.dataset.tissuMin || 1);
      const tissuTooLow = !!tissu && this.getQty('tissu') < tissuMin;
      const tissuAlert = this.querySelector('[data-kitc-tissu-alert]');
      if (tissuAlert) tissuAlert.hidden = !tissuTooLow;
      const boutonQtyRow = this.querySelector('[data-kitc-qtyrow="bouton"]');
      if (boutonQtyRow) boutonQtyRow.hidden = !bouton || !bouton.value;

      // Récap + total
      const recap = [];
      let total = 0;
      if (taille && !recharge) {
        total += Number(taille.dataset.price);
        const sizeLabel = this.querySelectorAll('input[data-kitc="taille"]').length > 1
          ? ` — ${taille.dataset.title}`
          : '';
        recap.push([`${this.dataset.patronTitle}${sizeLabel}`, Number(taille.dataset.price)]);
      }
      const metrageLabel = this.metrageLabel(this.getQty('tissu'));
      if (tissu) {
        const qty = this.getQty('tissu');
        total += Number(tissu.dataset.price) * qty;
        const suffix = metrageLabel ? ` (${metrageLabel})` : '';
        recap.push([`${tissu.dataset.title} × ${qty}${suffix}`, Number(tissu.dataset.price) * qty]);
      }
      if (bouton && bouton.value) {
        const qty = this.getQty('bouton');
        total += Number(bouton.dataset.price) * qty;
        recap.push([`${bouton.dataset.title} × ${qty}`, Number(bouton.dataset.price) * qty]);
      }
      if (fil && fil.value) {
        total += Number(fil.dataset.price);
        recap.push([fil.dataset.title, Number(fil.dataset.price)]);
      }
      this.merceries().forEach(({ title, price, qty }) => {
        total += price * qty;
        recap.push([`${title} × ${qty}`, price * qty]);
      });
      if (etiquette && etiquette.value) {
        total += Number(etiquette.dataset.price);
        recap.push([etiquette.dataset.title, Number(etiquette.dataset.price)]);
      }
      if (tissu && this.dataset.cadeauVariant && !recharge) {
        recap.push([`🎁 ${this.dataset.cadeauTitle}`, this.dataset.cadeauLabel || 'Offert']);
      }

      const recapEl = this.querySelector('[data-kitc-recap]');
      if (recapEl) {
        recapEl.textContent = '';
        recap.forEach(([label, price]) => {
          const li = document.createElement('li');
          const name = document.createElement('span');
          name.textContent = label;
          const amount = document.createElement('span');
          amount.textContent = typeof price === 'number' ? this.formatter.format(price / 100) : price;
          li.append(name, amount);
          recapEl.append(li);
        });
      }

      const metrageEl = this.querySelector('[data-kitc-metrage]');
      if (metrageEl) metrageEl.textContent = metrageLabel ? `= ${metrageLabel}` : '';

      const totalEl = this.querySelector('[data-kitc-total]');
      if (totalEl) totalEl.textContent = tissu ? this.formatter.format(total / 100) : '—';

      const totalTexte = tissu ? this.formatter.format(total / 100) : '—';
      const complet = !!tissu && (recharge || !!taille) && !tissuTooLow;

      const atc = this.querySelector('[data-kitc-atc]');
      if (atc && !atc.dataset.loading) atc.disabled = !complet;

      this.syncBar({ tissu, bouton, totalTexte });
    }

    syncBar({ tissu, bouton, totalTexte }) {
      const bar = this.querySelector('[data-kitc-bar]');
      if (!bar) return;

      bar.hidden = !tissu || this.recapVisible;
      if (bar.hidden) return;

      // vignettes du tissu et des boutons choisis
      this.syncBarThumb(bar, '[data-kitc-bar-tissu]', tissu);
      this.syncBarThumb(bar, '[data-kitc-bar-bouton]', bouton);

      const totalEl = bar.querySelector('[data-kitc-bar-total]');
      if (totalEl) totalEl.textContent = totalTexte;
    }

    syncBarThumb(bar, selector, input) {
      const thumb = bar.querySelector(selector);
      if (!thumb) return;
      const image = input && input.value ? input.dataset.image : null;
      if (image) {
        const img = thumb.querySelector('img');
        if (img.getAttribute('src') !== image) img.src = image;
        // le bandeau n'a plus de texte : l'info passe par l'alternative textuelle
        img.alt = input.dataset.title || '';
        thumb.hidden = false;
      } else {
        thumb.hidden = true;
      }
    }

    async nextKitNumber() {
      // Numérote les kits d'un même panier pour que la préparation distingue
      // deux kits, y compris deux kits du même patron.
      // On repart du plus grand numéro présent (et non du nombre de kits) :
      // sinon, supprimer un kit du panier ferait réattribuer un numéro déjà utilisé.
      try {
        const cart = await fetch('/cart.js', {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        }).then((r) => r.json());

        let max = 0;
        (cart.items || []).forEach((item) => {
          Object.entries(item.properties || {}).forEach(([key, value]) => {
            if (!key.startsWith('Kit')) return;
            // le numéro est dans la clé ; on lit aussi la valeur pour les
            // paniers composés avant ce changement de format
            const trouve = key.match(/n°(\d+)/) || String(value).match(/n°(\d+)/);
            if (trouve) max = Math.max(max, Number(trouve[1]));
          });
        });
        return max + 1;
      } catch (err) {
        return 1; // un panier illisible ne doit jamais empêcher l'ajout
      }
    }

    async addKit(btn) {
      const { taille, tissu, bouton, fil, etiquette, recharge } = this.selection();
      if ((!recharge && !taille) || !tissu) return;
      if (this.getQty('tissu') < Number(this.dataset.tissuMin || 1)) return;

      const textEl = btn.querySelector('.kitc-atc__text');
      const addedEl = btn.querySelector('.kitc-atc__added');
      const loadingEl = btn.querySelector('.kitc-atc__loading');
      const errorEl = this.querySelector('[data-kitc-error]');

      btn.dataset.loading = '1';
      btn.disabled = true;
      if (errorEl) errorEl.hidden = true;
      if (textEl) textEl.style.display = 'none';
      if (loadingEl) loadingEl.style.display = '';

      // Une seule propriété, lisible telle quelle dans la commande.
      // C'est le numéro qui rend chaque kit unique : sans lui, deux kits
      // identiques verraient leurs lignes fusionnées par Shopify.
      const numero = await this.nextKitNumber();
      const properties = {
        [`${recharge ? 'Kit recharge' : 'Kit'} n°${numero}`]: this.dataset.patronTitle,
      };
      const items = [
        { id: Number(tissu.value), quantity: this.getQty('tissu'), properties },
      ];
      if (!recharge && taille) items.unshift({ id: Number(taille.value), quantity: 1, properties });
      if (bouton && bouton.value) items.push({ id: Number(bouton.value), quantity: this.getQty('bouton'), properties });
      if (fil && fil.value) items.push({ id: Number(fil.value), quantity: 1, properties });
      this.merceries().forEach(({ id, qty }) => items.push({ id, quantity: qty, properties }));
      if (etiquette && etiquette.value) items.push({ id: Number(etiquette.value), quantity: 1, properties });
      if (!recharge && this.dataset.cadeauVariant) items.push({ id: Number(this.dataset.cadeauVariant), quantity: 1, properties });

      const deferredEventPromise = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        promise: deferredEventPromise.promise,
      }));

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items }),
      })
        .then((r) => r.json().then((data) => {
          if (!r.ok) throw new Error(data.description || 'Erreur panier');
          return data;
        }))
        .then(() => fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then((r) => r.json()))
        .then((cart) => {
          deferredEventPromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: false },
          });
          if (loadingEl) loadingEl.style.display = 'none';
          if (addedEl) addedEl.style.display = '';
          btn.classList.add('is-added');
          setTimeout(() => {
            if (addedEl) addedEl.style.display = 'none';
            if (textEl) textEl.style.display = '';
            btn.classList.remove('is-added');
            delete btn.dataset.loading;
            btn.disabled = false;
            this.sync();
          }, 2500);
        })
        .catch((err) => {
          console.error('[Kit couture] Erreur ATC :', err.message);
          deferredEventPromise.reject(err);
          if (loadingEl) loadingEl.style.display = 'none';
          if (textEl) textEl.style.display = '';
          if (errorEl) {
            errorEl.textContent = err.message;
            errorEl.hidden = false;
          }
          delete btn.dataset.loading;
          btn.disabled = false;
          this.sync();
        });
    }
  }
  customElements.define('kit-couture-builder', KitCoutureBuilder);
}

if (!customElements.get('kit-patron-picker')) {
  class KitPatronPicker extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        const radio = e.target.closest('input[data-kitc-patron]');
        if (radio) this.reveal(radio);
      });

      this.addEventListener('click', (e) => {
        // pop-up « En savoir plus »
        const more = e.target.closest('[data-kitc-more]');
        if (more) {
          const dialog = this.querySelector(`#${CSS.escape(more.dataset.kitcMore)}`);
          if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
          return;
        }
        const close = e.target.closest('[data-kitc-close]');
        if (close) {
          const dialog = close.closest('dialog');
          if (dialog) dialog.close();
          return;
        }
        // clic sur le fond : fermer
        if (e.target.tagName === 'DIALOG') {
          e.target.close();
          return;
        }

        const btn = e.target.closest('[data-kitc-filter]');
        if (btn) this.filter(btn);
      });

      // Patron déjà coché au rendu (cas d'un seul patron proposé, fiche tissu)
      const dejaCoche = this.querySelector('input[data-kitc-patron]:checked');
      if (dejaCoche) this.reveal(dejaCoche);

      // pré-sélection via ?patron=<handle> dans l'URL
      const handle = new URLSearchParams(window.location.search).get('patron');
      if (handle) {
        const radio = this.querySelector(`input[data-kitc-patron][data-handle="${CSS.escape(handle)}"]`);
        if (radio && !radio.disabled) {
          radio.checked = true;
          this.reveal(radio);
        }
      }
    }

    reveal(radio) {
      this.querySelectorAll('[data-for-patron]').forEach((wrapper) => {
        wrapper.hidden = wrapper.dataset.forPatron !== radio.dataset.kitcPatron;
      });
    }

    filter(btn) {
      const type = btn.dataset.kitcFilter;
      this.querySelectorAll('[data-kitc-filter]').forEach((b) => {
        b.classList.toggle('is-active', b === btn);
      });

      let selectionMasquee = false;
      this.querySelectorAll('.kitc-grid--patrons .kitc-patron-cell').forEach((card) => {
        const visible = !type || (card.dataset.types || '').includes(`~${type}~`);
        card.hidden = !visible;
        const input = card.querySelector('input[data-kitc-patron]');
        if (!visible && input && input.checked) {
          input.checked = false;
          selectionMasquee = true;
        }
      });

      // Le patron sélectionné vient d'être filtré : refermer son module
      if (selectionMasquee) {
        this.querySelectorAll('[data-for-patron]').forEach((wrapper) => {
          wrapper.hidden = true;
        });
      }
    }
  }
  customElements.define('kit-patron-picker', KitPatronPicker);
}
